import React, { FC } from 'react';
import { useTranslation } from '@edreamsodigeo/frontend-core';
import {
  Col,
  Flex,
  FlexRow,
  Glyph,
  HighlightedHeader,
} from '@edreamsodigeo/prisma-design-system';
import { DealHeader } from '../Deals/DealHeader';
import {
  DEAL_HOTTEST,
  DEAL_WEEKEND,
} from 'clientState/hooks/useGetBuyPathDeal';
import Reprice from '../Reprice';
import useDisplayTypeBySite from 'clientState/hooks/useDisplayTypeBySite/useDisplayTypeBySite';
import {
  HighlightedType,
  highlightedTypesKeyMap,
} from './constants/highlightedTypes';

const DealIcon = () => <Glyph category="Deals" name="TopSaver" size="medium" />;
const CheapestIcon = () => <Glyph category="Finances" name="Money" />;
const TimeIcon = () => <Glyph category="TimeAndDates" name="Time" />;
const MetaSelectionIcon = () => (
  <Glyph
    category="UIActions"
    name="EyeOn"
    size="medium"
    data-testid="eye-on-offer"
    sx={{ position: 'relative', top: '2px' }}
  />
);
const PersonalisedIcon = () => (
  <Glyph category="Deals" name="TopRated" size="medium" />
);

const iconTypes = {
  fastest: TimeIcon,
  cheapest: CheapestIcon,
  deal: DealIcon,
  metaSelection: MetaSelectionIcon,
  personalised: PersonalisedIcon,
};

type OptionalIconProps = {
  type: string;
};

type HighlightedMessageProps = {
  type: string;
};

export type HeaderProps = {
  highlightedType: HighlightedType;
  sx?: Record<string, unknown> | undefined;
};

const OptionalIcon: FC<OptionalIconProps> = ({ type }) => {
  const Icon = iconTypes[type];
  return Icon ? (
    <Col>
      <Icon size="small" type={type} />
    </Col>
  ) : null;
};

const HighlightedMessageIcon: FC<HighlightedMessageProps> = ({ type }) => {
  if (type === DEAL_WEEKEND || type === DEAL_HOTTEST) {
    return <OptionalIcon type="deal" />;
  }
  if (type === HighlightedType.Personalised) {
    return <OptionalIcon type="personalised" />;
  } else if (type === HighlightedType.MetaSelection) {
    return <OptionalIcon type="metaSelection" />;
  }

  return null;
};

const HighlightedMessageDefault: FC<HighlightedMessageProps> = ({ type }) => {
  return (
    <FlexRow
      alignItems="center"
      gap={
        type === DEAL_WEEKEND || type === DEAL_HOTTEST ? '1x_small' : '2x_small'
      }
    >
      <HighlightedMessageIcon type={type} />
      <Col>
        <HighlightedMessageText type={type} />
      </Col>
    </FlexRow>
  );
};

const HighlightedMessageHeader: FC<HighlightedMessageProps> = ({ type }) => {
  return (
    <Flex id="MYID" width="100%" justifyContent="space-between">
      <FlexRow
        flexDirection="row"
        alignItems="center"
        gap={type === 'deal' ? '1x_small' : '2x_small'}
      >
        <HighlightedMessageIcon type={type} />
        <Col>
          <HighlightedMessageText type={type} />
        </Col>
      </FlexRow>
      <FlexRow flexDirection="row">
        <Reprice />
      </FlexRow>
    </Flex>
  );
};

const HighlightedMessage: FC<HighlightedMessageProps> = ({ type }) => {
  const { isUKDisplayType } = useDisplayTypeBySite();
  return !isUKDisplayType() ? (
    <HighlightedMessageHeader type={type} />
  ) : (
    <HighlightedMessageDefault type={type} />
  );
};

const HighlightedMessageText: FC<HighlightedMessageProps> = ({ type }) => {
  const { t } = useTranslation();
  return <>{t(`results.highlighted.itinerary.${type}`)}</>;
};

const HeaderComponent: FC<HeaderProps> = ({ highlightedType, children }) => {
  const PromotedHeader =
    highlightedType === DEAL_WEEKEND || highlightedType === DEAL_HOTTEST
      ? DealHeader
      : HighlightedHeader;

  return (
    <PromotedHeader
      type={highlightedTypesKeyMap[highlightedType].type}
      size={highlightedTypesKeyMap[highlightedType].size}
      borderSize={highlightedTypesKeyMap[highlightedType].border}
      sx={{
        paddingBottom:
          highlightedTypesKeyMap[highlightedType].padding || '1x_small',
        backgroundColor:
          highlightedTypesKeyMap[highlightedType].backgroundColor,
        color: highlightedTypesKeyMap[highlightedType].color,
        marginBottom: highlightedTypesKeyMap[highlightedType].marginBottom,
      }}
    >
      {children}
    </PromotedHeader>
  );
};
const ItineraryHeader: FC<HeaderProps> = ({ highlightedType }) => {
  return (
    <HeaderComponent
      highlightedType={highlightedType}
      sx={{
        paddingBottom: '2x_small',
      }}
    >
      <HighlightedMessage type={highlightedType} />
    </HeaderComponent>
  );
};
export default ItineraryHeader;
